import React, { useState, useEffect } from "react";
import { TableIcon, Plus, Edit, Trash2 } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { message } from "../../components/Common/SnakeAlert/SnakeAlert";
import EditShiftModal from "./EditShiftModal";
import AddShiftModal from "./AddShiftModal";
import DeleteShiftModal from "./DeleteShiftModal";
import graphqlRequest from "../../requestMethod";
import "./ShiftManagementTable.scss";

const pocOptions = [
  "Pratika - Head Nurse (pratika@hospital.com)",
  "Dr. Johnson - Department Head (johnson@hospital.com)",
  "Sarah Wilson - Shift Supervisor (sarah@hospital.com)",
  "Dr. Michael Brown - Chief Medical Officer (mbrown@hospital.com)",
];

const ShiftManagementTable = ({
  professionalDetails,
  jobDescription,
  minimumHour,
  maximumHour,
  minJobRate,
}) => {
  const [editingShift, setEditingShift] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [shiftToDelete, setShiftToDelete] = useState(null);
  const [shiftIndexToDelete, setShiftIndexToDelete] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [fetchContactDetails, setFetchContactDetails] = useState([]);
  const [editForm, setEditForm] = useState({
    startDate: "",
    startTime: "",
    duration: "",
    rateOffered: "",
    campDuty: false,
    pocDetails: "",
  });
  const [addForm, setAddForm] = useState({
    startDate: "",
    startTime: "",
    duration: "",
    rateOffered: "",
    campDuty: false,
    pocDetails: "",
  });

  let loginData = localStorage.getItem("auth");
  let token = JSON.parse(loginData)?.accessToken;

  // console.log("professionalDetails shift table", professionalDetails);
  function fetchContactDetailsUser() {
    const query = `
         query{
           withAuth(token:"${token}"){
             listOrganizationUsers {
               id
               email
               contact
               name
             }
            }
          }
       `;
    graphqlRequest(query).then((response) => {
      // console.log("response", response.data.withAuth.listOrganizationUsers);
      setFetchContactDetails(response.data.withAuth.listOrganizationUsers);
    });
  }
  useEffect(() => {
    fetchContactDetailsUser();
  }, []);

  // useEffect(() => {
  //   if (showEditDialog) {
  //     document.body.style.overflow = "hidden"; // disable scroll
  //     document.body.style.pointerEvents = "none"; // block clicks
  //   } else {
  //     document.body.style.overflow = "auto";
  //     document.body.style.pointerEvents = "auto";
  //   }

  //   // Cleanup on unmount
  //   return () => {
  //     document.body.style.overflow = "auto";
  //     document.body.style.pointerEvents = "auto";
  //   };
  // }, [showEditDialog]);

  const queryClient = useQueryClient();

  //  const { data: shifts = [], isLoading } = {}
  const { data: shifts = [], isLoading } = useQuery({
    queryKey: ["/api/shifts"],
    queryFn: async () => {
      const response = await fetch("/api/shifts");
      if (!response.ok) throw new Error("Failed to fetch shifts");
      return response.json();
    },
  });

  const now = new Date();
  const today = now.toISOString().split("T")[0];
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();

  const generateTimeOptions = () => {
    const options = [];
    try {
      for (let hour = 0; hour < 24; hour++) {
        for (let minute = 0; minute < 60; minute += 30) {
          const timeString = `${hour.toString().padStart(2, "0")}:${minute
            .toString()
            .padStart(2, "0")}`;
          options.push(timeString);
        }
      }
    } catch (error) {
      console.error("Error generating time options:", error);
    }
    return options;
  };

  const timeOptions = generateTimeOptions();

  // const updateShift = ""
  // const deleteShift = ""
  // const addShift = ""

  const updateShift = useMutation({
    mutationFn: async ({ index, data }) => {
      const currentShifts = queryClient.getQueryData(["/api/shifts"]) || [];
      const updatedShifts = [...currentShifts];
      updatedShifts[index] = { ...updatedShifts[index], ...data };

      // Update the cache
      queryClient.setQueryData(["/api/shifts"], updatedShifts);

      return updatedShifts;
    },
    onSuccess: () => {
      setShowEditDialog(false);
      setEditingShift(null);
    },
  });

  const deleteShift = useMutation({
    mutationFn: async (indexToDelete) => {
      const currentShifts = queryClient.getQueryData(["/api/shifts"]) || [];
      const updatedShifts = currentShifts.filter((_, i) => i !== indexToDelete);
      queryClient.setQueryData(["/api/shifts"], updatedShifts);
      return updatedShifts;
    },
    onSuccess: () => {
      setShowDeleteDialog(false);
      setShiftToDelete(null);
      setShiftIndexToDelete(null);
    },
  });

  const addShift = useMutation({
    mutationFn: async (shiftData) => {
      const currentShifts = queryClient.getQueryData(["/api/shifts"]) || [];
      const updatedShifts = [...currentShifts, shiftData];
      queryClient.setQueryData(["/api/shifts"], updatedShifts);
      return updatedShifts;
    },
    onSuccess: () => {
      setShowAddDialog(false);
      setAddForm({
        startDate: "",
        startTime: "",
        duration: "",
        rateOffered: "",
        campDuty: false,
        professionalCount: 0,
        pocDetails: "",
      });
    },
    onError: (error) => {
      console.error("Error adding shift:", error);
      alert("Failed to add shift.");
    },
  });

  const handleEdit = (shift, index) => {
    setEditingShift(shift);
    setEditingIndex(index);
    setEditForm({
      startDate: shift.startDate || "",
      startTime: shift.startTime || "",
      duration: shift.duration || "",
      rateOffered: shift.rateOffered || "",
      campDuty: shift.campDuty || false,
      pocDetails: shift.pocDetails || "",
    });
    setShowEditDialog(true);
  };

  const handleDeleteClick = (shift, index) => {
    setShiftToDelete(shift); // pass the shift object for modal preview
    setShiftIndexToDelete(index); // keep index for deletion
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    if (shiftIndexToDelete !== null) {
      deleteShift.mutate(shiftIndexToDelete);
    }
  };

  const handleSaveEdit = (index, updatedData) => {
    console.log("updated", updatedData);
    const duration = parseInt(updatedData.duration);
    if (duration < minimumHour || duration > maximumHour) {
      message.error(
        `Duration must be between ${minimumHour} and ${maximumHour} hours.`
      );
      return;
    }

    if (updatedData.rateOffered < minJobRate) {
      message.error(
        `Rate Offered Per Hour must be greater than ₹${minJobRate}`
      );
      return;
    }

    const selectedDate = new Date(updatedData.startDate);
    const [hours, minutes] = updatedData.startTime.split(":");
    selectedDate.setHours(Number(hours));
    selectedDate.setMinutes(Number(minutes));
    selectedDate.setSeconds(0);

    const now = new Date();

    if (selectedDate < now) {
      message.error("Select time");
      return;
    }
    // // Step 1: Ensure endDate is same as startDate
    // const updatedStartDate = updatedData.startDate;
    // const updatedEndDate = updatedStartDate; // endDate will match startDate

    // // Step 2: Calculate endTime based on startTime + duration
    // const [startHour, startMinute] = updatedData.startTime
    //   .split(":")
    //   .map(Number);
    // const startTimeDate = new Date(`1970-01-01T${updatedData.startTime}:00`);
    // startTimeDate.setHours(startHour + duration);
    // const endHour = startTimeDate.getHours().toString().padStart(2, "0");
    // const endMinute = startTimeDate.getMinutes().toString().padStart(2, "0");
    // const updatedEndTime = `${endHour}:${endMinute}`;

    const startDate = new Date(updatedData.startDate); // Date object
    const [startHour, startMinute] = updatedData.startTime
      .split(":")
      .map(Number);

    // Create full Date object with startDate and startTime
    const startDateTime = new Date(startDate);
    startDateTime.setHours(startHour);
    startDateTime.setMinutes(startMinute);
    startDateTime.setSeconds(0);

    // Add duration
    const endDateTime = new Date(
      startDateTime.getTime() + duration * 60 * 60 * 1000
    );

    // Format end time as HH:mm
    const endHour = endDateTime.getHours().toString().padStart(2, "0");
    const endMinute = endDateTime.getMinutes().toString().padStart(2, "0");
    const updatedEndTime = `${endHour}:${endMinute}`;

    // Format end date as YYYY-MM-DD
    const updatedEndDate =
      endDateTime.getFullYear() +
      "-" +
      String(endDateTime.getMonth() + 1).padStart(2, "0") +
      "-" +
      String(endDateTime.getDate()).padStart(2, "0");

    console.log("updatedEndDate", updatedEndDate);

    const finalData = {
      ...updatedData,
      endDate: updatedEndDate,
      endTime: updatedEndTime,
    };

    console.log("updatedData (final)", finalData);

    // console.log("updatedData", updatedData);

    updateShift.mutate(
      { index, data: finalData },
      {
        onSuccess: () => {
          // ✅ Update the cache immediately after successful update
          const currentShifts = queryClient.getQueryData(["/api/shifts"]) || [];

          const updatedShifts = currentShifts.map((shift, idx) =>
            idx === index ? { ...shift, ...finalData } : shift
          );

          queryClient.setQueryData(["/api/shifts"], updatedShifts);
          message.success("Shift updated successfully.");
        },
        onError: () => {
          message.error("Failed to update shift.");
        },
      }
    );
    // updateShift.mutate({
    //   index,
    //   data: updatedData,
    // });
  };

  const handleAddShift = () => {
    const duration = parseInt(addForm.duration);

    if (
      !addForm.startDate ||
      !addForm.startTime ||
      !addForm.duration ||
      !addForm.rateOffered
    ) {
      message.error("Please fill in all fields before adding the shift.");
      return;
    }

    if (addForm.rateOffered < minJobRate) {
      message.error(
        `Rate Offered Per Hour must be greater than ₹${minJobRate}`
      );
      return;
    }

    if (duration < minimumHour || duration > maximumHour) {
      message.error(
        `Duration must be between ${minimumHour} and ${maximumHour} hours.`
      );
      return;
    }

    const selectedDate = new Date(addForm.startDate);
    const [hours, minutes] = addForm.startTime.split(":");
    selectedDate.setHours(Number(hours));
    selectedDate.setMinutes(Number(minutes));
    selectedDate.setSeconds(0);

    const now = new Date();

    if (selectedDate < now) {
      message.error("Select time");
      return;
    }

    const [startHour, startMinute] = addForm.startTime.split(":").map(Number);
    const shiftStart = new Date(addForm.startDate);
    shiftStart.setHours(startHour, startMinute, 0, 0);

    const shiftEnd = new Date(
      shiftStart.getTime() + addForm.duration * 60 * 60 * 1000
    );
    // console.log("shiftEnd", shiftEnd);
    const endTime = shiftEnd.toTimeString().slice(0, 5);

    const finalEndDate =
      shiftEnd.getFullYear() +
      "-" +
      String(shiftEnd.getMonth() + 1).padStart(2, "0") +
      "-" +
      String(shiftEnd.getDate()).padStart(2, "0");

    // console.log("finalEndDate", finalEndDate);

    addShift.mutate({
      startDate: addForm.startDate,
      endDate: finalEndDate,
      startTime: addForm.startTime,
      endTime: endTime,
      duration: parseInt(addForm.duration) || 0,
      rateOffered: addForm.rateOffered,
      campDuty: addForm.campDuty,
      pocDetails: addForm.pocDetails,
      professionalCount: 1,
      hospital: professionalDetails?.hospital,
      hospitalName: professionalDetails?.hospitalName,
      department: professionalDetails?.department,
      departmentName: professionalDetails?.departmentName,
      profession: professionalDetails?.profession,
      professionName: professionalDetails?.professionName,
      qualification: professionalDetails?.qualification,
      specialization: professionalDetails?.specialization,
      experience: professionalDetails?.experience,
      skillSet: professionalDetails?.skillSet,
      recurringDays: [],
      shiftType: "CONSECUTIVE",
      jobDescription: jobDescription,
    });
  };

  const formatDateTime = (date, time) => {
    try {
      const dateObj = new Date(date);
      const [hours, minutes] = time.split(":");
      dateObj.setHours(parseInt(hours), parseInt(minutes));

      return dateObj.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return `${date} ${time}`;
    }
  };

  // const calculateEndTime = (startDate, startTime, duration) => {
  //   try {
  //     console.log("durationCalc", duration);
  //     const dateObj = new Date(startDate);
  //     const [hours, minutes] = startTime.split(":");
  //     dateObj.setHours(parseInt(hours), parseInt(minutes));
  //     dateObj.setHours(dateObj.getHours() + duration);

  //     return dateObj.toLocaleDateString("en-US", {
  //       month: "short",
  //       day: "numeric",
  //       year: "numeric",
  //       hour: "numeric",
  //       minute: "2-digit",
  //       hour12: true,
  //     });
  //   } catch {
  //     return `${startDate} ${startTime}`;
  //   }
  // };

  // if (isLoading) {
  //   return (
  //     <div className="card-modern shift-management-table">
  //       <div className="p-6">
  //         <div className="loading-state">
  //           <div className="loading-text">Loading shifts...</div>
  //         </div>
  //       </div>
  //     </div>
  //   );
  // }

  const calculateEndTime = (startDate, startTime, duration) => {
    try {
      console.log("durationCalc", duration);
      const dateObj = new Date(startDate);
      const [hours, minutes] = startTime.split(":");
      dateObj.setHours(parseInt(hours), parseInt(minutes));

      const wholeHours = Math.floor(duration); // e.g. 5.5 → 5
      const extraMinutes = Math.round((duration - wholeHours) * 60); // e.g. 0.5 → 30

      dateObj.setHours(dateObj.getHours() + wholeHours);
      dateObj.setMinutes(dateObj.getMinutes() + extraMinutes);

      return dateObj.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return `${startDate} ${startTime}`;
    }
  };


  console.log("inside shiftManagementTable", shifts);
  // console.log("editingIndex", editingIndex);
  // console.log("editingShift", editingShift);
  return (
    <div className="card-modern shift-management-table">
      <div className="p-6">
        <div className="table-header">
          <div className="component-header">
            <div className="icon-container">
              <TableIcon size={20} />
            </div>
            <h2>Shift Management</h2>
          </div>
          <button
            // onClick={() => setShowAddDialog(true)}
            onClick={() => {
              if (
                !professionalDetails.profession ||
                !professionalDetails.hospital ||
                !professionalDetails.department ||
                !professionalDetails.qualification ||
                !professionalDetails.specialization ||
                !professionalDetails.experience
              ) {
                message.error(
                  "Please fill in professional details before adding the shift."
                );
                return;
              }

              setShowAddDialog(true); // only show dialog if all fields are filled
            }}
            className="btn2 btn-accent2"
          >
            <Plus className="icon" />
            Add New Shift
          </button>
        </div>

        {shifts.length === 0 ? (
          <div className="empty-state">
            <p className="empty-message">No shifts have been created yet.</p>
            <p className="empty-submessage">
              Use the form above to create your first shift or click "Add New
              Shift" to add manually.
            </p>
          </div>
        ) : (
          <>
            {/* Mobile Card Layout */}
            <div className="mobile-shifts">
              {shifts.map((shift, index) => (
                <div key={index} className="shift-card">
                  <div className="shift-card-header">
                    <div className="shift-times">
                      <div className="start-time">
                        {formatDateTime(shift.startDate, shift.startTime)}
                      </div>
                      <div className="end-time">
                        Ends: {calculateEndTime(
                          shift.startDate,
                          shift.startTime,
                          shift.duration
                        )}
                      </div>
                    </div>
                    <div className="action-buttons">
                      <button
                        onClick={() => handleEdit(shift, index)}
                        className="btn btn-ghost btn-sm"
                      >
                        <Edit className="action-icon" />
                      </button>
                      <button
                        onClick={() => handleDeleteClick(shift, index)}
                        className="btn btn-ghost btn-sm delete-btn"
                      >
                        <Trash2 className="action-icon" />
                      </button>
                    </div>
                  </div>
                  <div className="shift-details">
                    <div className="detail-item">
                      <div className="detail-label">Professionals</div>
                      <div className="detail-value">{shift.professionalCount}</div>
                    </div>
                    <div className="detail-item">
                      <div className="detail-label">Rate Offered</div>
                      <div className="detail-value">₹{shift.rateOffered}/hr</div>
                    </div>
                    <div className="detail-item camp-duty-item">
                      <div className="detail-label">Camp Duty</div>
                      <div className="detail-value">
                        <span
                          className={`badge ${
                            shift.campDuty ? "badge-accent" : "badge-secondary"
                          }`}
                        >
                          {shift.campDuty ? "Yes" : "No"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table Layout */}
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Start Date & Time</th>
                    <th>End Date & Time</th>
                  {/* <th>Hospital</th>
                  <th>Department</th>
                  <th>Profession</th> */}
                    <th>No. of Professionals</th>
                    <th>Rate Offered</th>
                    <th>Camp Duty</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {shifts.map((shift, index) => (
                    <tr key={index}>
                      <td>{formatDateTime(shift.startDate, shift.startTime)}</td>
                      <td>
                        {calculateEndTime(
                          shift.startDate,
                          shift.startTime,
                          shift.duration
                        )}
                      </td>
                    {/* <td>{shift.hospitalName}</td>
                    <td>{shift.departmentName}</td>
                    <td>{shift.professionName}</td> */}
                      <td>{shift.professionalCount}</td>
                      <td>₹{shift.rateOffered}/hr</td>
                      <td>
                        <span
                          className={`badge ${
                            shift.campDuty ? "badge-accent" : "badge-secondary"
                          }`}
                        >
                          {shift.campDuty ? "Yes" : "No"}
                        </span>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            onClick={() => handleEdit(shift, index)}
                            className="btn btn-ghost btn-sm"
                          >
                            <Edit className="action-icon" />
                          </button>
                          <button
                            onClick={() => handleDeleteClick(shift, index)}
                            className="btn btn-ghost btn-sm delete-btn"
                          >
                            <Trash2 className="action-icon" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Add Shift Modal */}
      <AddShiftModal
        open={showAddDialog}
        onClose={() => setShowAddDialog(false)}
        addForm={addForm}
        setAddForm={setAddForm}
        today={today}
        timeOptions={timeOptions}
        currentHour={currentHour}
        currentMinute={currentMinute}
        minimumHour={minimumHour}
        maximumHour={maximumHour}
        handleAddShift={handleAddShift}
        addShift={addShift}
      />
      {/* {showAddDialog && (
        <div className="modal-overlay" onClick={() => setShowAddDialog(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add New Shift</h3>
              <button
                onClick={() => setShowAddDialog(false)}
                className="modal-close"
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="grid grid-2">
                <div className="form-group">
                  <label className="label">Start Date</label>
                  <input
                    type="date"
                    min={today}
                    value={addForm.startDate}
                    onChange={(e) =>
                      setAddForm({ ...addForm, startDate: e.target.value })
                    }
                    className="input"
                  />
                </div>
                <div className="form-group">
                  <label className="label">Start Time</label>
                  <select
                    value={addForm.startTime}
                    onChange={(e) =>
                      setAddForm({ ...addForm, startTime: e.target.value })
                    }
                    style={{
                      backgroundColor: "#f0f9ff",
                      height: "53px",
                      borderRadius: "15px",
                      marginTop: "10px",
                    }}
                  >
                    <option value="">Select time</option>
                    {timeOptions.map((time) => {
                      const isToday = addForm.startDate === today;
                      const [hour, minute] = time.split(":").map(Number);
                      const timeInMinutes = hour * 60 + minute;
                      const currentTimeInMinutes =
                        currentHour * 60 + currentMinute;
                      const isInFuture =
                        !isToday || timeInMinutes > currentTimeInMinutes;

                      return isInFuture ? (
                        <option key={time} value={time}>
                          {time}
                        </option>
                      ) : null; // Skip rendering this option
                    })}
                  </select>
               
                </div>
                <div className="form-group">
                  <label className="label">
                    No. of Hours ({minimumHour} - {maximumHour})
                  </label>
                  <input
                    type="number"
                    min={minimumHour}
                    max={maximumHour}
                    value={addForm.duration}
                    onChange={(e) => {
                      const rawValue = e.target.value;

                      // Allow blank string for typing/deleting
                      if (rawValue === "") {
                        setAddForm({ ...addForm, duration: "" });
                        return;
                      }

                      const value = Number(rawValue);
                      setAddForm({ ...addForm, duration: rawValue });
                    }}
                    // onBlur={() => {
                    //   const value = Number(addForm.duration);
                    //   if (value < minimumHour || value > maximumHour) {
                    //     message.error(
                    //       `Value must be between ${minimumHour} and ${maximumHour}`
                    //     );
                    //     setAddForm({ ...addForm, duration: "" }); // or reset to minimumHour
                    //   }
                    // }}
                    // onChange={(e) =>
                    //   setAddForm({ ...addForm, duration: e.target.value })
                    // }
                    className="input"
                    placeholder={`Enter duration between ${minimumHour}-${maximumHour} hrs`}
                  />
                </div>
                <div className="form-group">
                  <label className="label">Rate Offered</label>
                  <input
                    type="number"
                    value={addForm.rateOffered}
                    onChange={(e) =>
                      setAddForm({ ...addForm, rateOffered: e.target.value })
                    }
                    className="input"
                    placeholder="Enter rate offered"
                  />
                </div>
                <div className="form-group">
                  <label className="label">Camp Duty</label>
                  <div className="radio-group">
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="addCampDuty"
                        checked={addForm.campDuty === false}
                        onChange={() =>
                          setAddForm({ ...addForm, campDuty: false })
                        }
                      />
                      <span>No</span>
                    </label>
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="addCampDuty"
                        checked={addForm.campDuty === true}
                        onChange={() =>
                          setAddForm({ ...addForm, campDuty: true })
                        }
                      />
                      <span>Yes</span>
                    </label>
                  </div>
                </div>
               
               
              </div>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowAddDialog(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleAddShift}
                disabled={addShift.isPending}
                className="btn btn-primary"
              >
                {addShift.isPending ? "Adding..." : "Add Shift"}
              </button>
            </div>
          </div>
        </div>
      )} */}

      {/* Edit Shift Modal */}
      <EditShiftModal
        open={showEditDialog}
        onClose={() => setShowEditDialog(false)}
        editForm={editForm}
        setEditForm={setEditForm}
        today={today}
        timeOptions={timeOptions}
        currentHour={currentHour}
        currentMinute={currentMinute}
        minimumHour={minimumHour}
        maximumHour={maximumHour}
        handleSaveEdit={handleSaveEdit}
        editingIndex={editingIndex}
        updateShift={updateShift}
      />
      {/* {showEditDialog && (
        <div
          className="modal-overlay"
          style={{ zIndex: 1000 }}
          onClick={() => setShowEditDialog(false)}
        >
          <div
            className="modal-content"
            style={{ zIndex: 1001 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>Edit Shift</h3>
              <button
                onClick={() => setShowEditDialog(false)}
                className="modal-close"
                style={{ zIndex: 1002 }}
              >
                ×
              </button>
            </div>
            <div className="modal-body" style={{ zIndex: 1002 }}>
              <div className="grid grid-2">
                <div className="form-group">
                  <label className="label">Start Date</label>
                  <input
                    type="date"
                    min={today}
                    value={editForm.startDate}
                    onChange={(e) =>
                      setEditForm({ ...editForm, startDate: e.target.value })
                    }
                    className="input"
                  />
                </div>
                <div className="form-group">
                  <label className="label">Start Time</label>
                  <select
                    value={editForm.startTime}
                    onChange={(e) =>
                      setEditForm({ ...editForm, startTime: e.target.value })
                    }
                    style={{
                      backgroundColor: "#f0f9ff",
                      height: "53px",
                      borderRadius: "15px",
                      marginTop: "10px",
                    }}
                  >
                    <option value="">Select time</option>
                    {timeOptions.map((time) => {
                      const isToday = editForm.startDate === today;
                      const [hour, minute] = time.split(":").map(Number);
                      const timeInMinutes = hour * 60 + minute;
                      const currentTimeInMinutes =
                        currentHour * 60 + currentMinute;
                      const isInFuture =
                        !isToday || timeInMinutes > currentTimeInMinutes;

                      return isInFuture ? (
                        <option key={time} value={time}>
                          {time}
                        </option>
                      ) : null; // Skip rendering this option
                    })}
                  </select>
              
                </div>
                <div className="form-group">
                  <label className="label">
                    No. of Hours ({minimumHour} - {maximumHour})
                  </label>
                  <input
                    type="number"
                    min={minimumHour}
                    max={maximumHour}
                    value={editForm.duration}
                    onChange={(e) => {
                      const rawValue = e.target.value;

                      // Allow blank string for typing/deleting
                      if (rawValue === "") {
                        setEditForm({
                          ...editForm,
                          duration: "",
                        });
                        return;
                      }

                      const value = Number(rawValue);
                      setEditForm({
                        ...editForm,
                        duration: parseInt(rawValue),
                      });
                    }}
                
                    className="input"
                    placeholder={`Enter duration between ${minimumHour}-${maximumHour} hrs`}
                  />
                </div>
                <div className="form-group">
                  <label className="label">Rate Offered (per hour)</label>
                  <input
                    type="number"
                    min="0"
                    placeholder="Enter Rate Offered"
                    value={editForm.rateOffered}
                    onChange={(e) =>
                      setEditForm({ ...editForm, rateOffered: e.target.value })
                    }
                    className="input"
                  />
                </div>
                <div className="form-group">
                  <label className="label">Camp Duty</label>
                  <div className="radio-group">
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="editCampDuty"
                        checked={editForm.campDuty === false}
                        onChange={() =>
                          setEditForm({ ...editForm, campDuty: false })
                        }
                      />
                      <span>No</span>
                    </label>
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="editCampDuty"
                        checked={editForm.campDuty === true}
                        onChange={() =>
                          setEditForm({ ...editForm, campDuty: true })
                        }
                      />
                      <span>Yes</span>
                    </label>
                  </div>
                </div>
               
              </div>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowEditDialog(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={() => handleSaveEdit(editingIndex, editForm)}
                disabled={updateShift.isPending}
                className="btn btn-primary"
              >
                {updateShift.isPending ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )} */}

      {/* Delete Confirmation Modal */}

      <DeleteShiftModal
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        shiftToDelete={shiftToDelete}
        deleteShift={deleteShift}
        handleConfirmDelete={handleConfirmDelete}
        formatDateTime={formatDateTime}
      />
      {/* {showDeleteDialog && (
        <div
          className="modal-overlay"
          onClick={() => setShowDeleteDialog(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Confirm Deletion</h3>
              <button
                onClick={() => setShowDeleteDialog(false)}
                className="modal-close"
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>
                Are you sure you want to delete this shift? This action cannot
                be undone.
              </p>
              {shiftToDelete && (
                <div className="shift-preview">
                  <p>
                    <strong>Hospital:</strong> {shiftToDelete.hospitalName}
                  </p>
                  <p>
                    <strong>Date:</strong>{" "}
                    {formatDateTime(
                      shiftToDelete.startDate,
                      shiftToDelete.startTime
                    )}
                  </p>
                  <p>
                    <strong>Duration:</strong> {shiftToDelete.duration} hours
                  </p>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowDeleteDialog(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deleteShift.isPending}
                className="btn btn-accent"
              >
                {deleteShift.isPending ? "Deleting..." : "Delete Shift"}
              </button>
            </div>
          </div>
        </div>
      )} */}
    </div>
  );
};

export default ShiftManagementTable;
